import { H2, LoadingIndicator } from '@triumph/design-system';
import { isAxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type {
    EmailProduct,
    EmailTemplatePreview as Preview,
    PreviewSiteEnvironment,
} from '@/components/shared/api/endpoints/emailTemplates';
import { usePreviewEmailTemplateMutation } from '@/components/shared/api/mutations/emailTemplates';
import { useEmailTemplatesQuery } from '@/components/shared/api/queries/emailTemplates';

import {
    Controls,
    ModeBadge,
    Notice,
    PageLayout,
    TemplateButton,
    TemplateList,
} from './EmailTemplatePreview.StyledComponents';
import { EmailPreviewFrame } from './EmailPreviewFrame';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DEBOUNCE_MS = 300;
const PRODUCTS: { value: EmailProduct; label: string }[] = [
    { value: 'Factoring', label: 'Factoring' },
    { value: 'LoadPay', label: 'LoadPay bank account' },
    { value: 'TcsFuel', label: 'Fuel' },
];
const SITE_ENVIRONMENT_LABELS: Record<PreviewSiteEnvironment, string> = {
    Current: 'Current environment',
    Dev: 'Dev',
    Qa: 'QA',
    Prod: 'Prod',
};

const describeError = (error: unknown, brand: string, applicantMode: boolean): { notice?: string; applicantNotFound?: boolean } => {
    if (!isAxiosError(error)) return { notice: "Couldn't load the preview. Try again." };
    const status = error.response?.status;
    const data = error.response?.data as { missingTokens?: string[]; message?: string } | string[] | undefined;
    if (status === 404) return { applicantNotFound: true };
    if (status === 422 && data && typeof data === 'object' && 'missingTokens' in data && data.missingTokens) {
        const context = applicantMode ? 'this applicant' : brand;
        return { notice: `Can't render for ${context}. Missing tokens: ${data.missingTokens.join(', ')}` };
    }
    if (status === 422 && data && typeof data === 'object' && 'message' in data) return { notice: data.message };
    if (status === 400 && Array.isArray(data)) {
        return { notice: data.join(' ') };
    }
    if (status === 400) return { notice: "This combination can't be previewed" };
    return { notice: "Couldn't load the preview. Try again." };
};

export const EmailTemplatePreview = () => {
    const { data: catalog, isLoading } = useEmailTemplatesQuery();
    const { mutateAsync } = usePreviewEmailTemplateMutation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [preview, setPreview] = useState<Preview>();
    const [notice, setNotice] = useState<string>();
    const [applicantNotFound, setApplicantNotFound] = useState(false);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const latestRequestRef = useRef(0);

    const templates = catalog?.templates ?? [];
    const item = templates.find((t) => t.notificationType === searchParams.get('type')) ?? templates[0];
    const applicantId = searchParams.get('applicantId') ?? '';
    const applicantIdValid = GUID.test(applicantId);
    const urlBrand = searchParams.get('brand');
    const brand = item && urlBrand && item.brands.includes(urlBrand as never) ? urlBrand : item?.brands[0];
    const siteEnvironments = catalog?.siteEnvironments ?? [];
    const urlSite = searchParams.get('site');
    const siteEnvironment = siteEnvironments.find((s) => s === urlSite) ?? 'Current';
    const urlProducts = searchParams.get('products');
    const products = useMemo<EmailProduct[]>(
        () => (urlProducts ? (urlProducts.split(',') as EmailProduct[]) : item ? [item.requiredProduct] : []),
        [urlProducts, item],
    );

    const updateParams = (changes: Record<string, string | undefined>) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        if (item && urlBrand && brand !== urlBrand) updateParams({ brand });
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [item, urlBrand, brand]);

    useEffect(() => {
        if (!item || !brand) return undefined;
        if (applicantId && !applicantIdValid) return undefined;

        const timer = setTimeout(() => {
            const requestNumber = ++latestRequestRef.current;
            setIsLoadingPreview(true);
            mutateAsync({
                notificationType: item.notificationType,
                brand: brand as Preview['brandUsed'],
                products,
                siteEnvironment,
                ...(applicantId ? { applicantId } : {}),
            })
                .then((result) => {
                    if (requestNumber !== latestRequestRef.current) return;
                    setPreview(result);
                    setNotice(undefined);
                    setApplicantNotFound(false);
                    setIsLoadingPreview(false);
                })
                .catch((error: unknown) => {
                    if (requestNumber !== latestRequestRef.current) return;
                    const applicantMode = applicantId.length > 0;
                    const described = describeError(error, brand, applicantMode);
                    setNotice(described.notice);
                    setApplicantNotFound(Boolean(described.applicantNotFound));
                    if (!described.applicantNotFound) {
                        setPreview(undefined);
                    }
                    setIsLoadingPreview(false);
                });
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [item, brand, products, siteEnvironment, applicantId, applicantIdValid, mutateAsync]);

    if (isLoading) return <LoadingIndicator show />;

    const applicantMode = applicantId.length > 0;

    return (
        <PageLayout data-testid="email-template-preview">
            <TemplateList>
                {templates.map((t) => (
                    <TemplateButton
                        key={t.notificationType}
                        type="button"
                        aria-pressed={t.notificationType === item?.notificationType}
                        onClick={() => updateParams({ type: t.notificationType, brand: undefined, products: undefined })}
                    >
                        {t.name}
                    </TemplateButton>
                ))}
            </TemplateList>
            <div>
                <H2>Email Template Preview</H2>
                <Controls>
                    <label>
                        Brand{' '}
                        <select
                            data-testid="email-preview-brand"
                            value={brand}
                            disabled={applicantMode || (item?.brands.length ?? 0) <= 1}
                            onChange={(e) => updateParams({ brand: e.target.value })}
                        >
                            {item?.brands.map((b) => (
                                <option key={b} value={b}>
                                    {b}
                                </option>
                            ))}
                        </select>
                    </label>
                    {PRODUCTS.map((p) => (
                        <label key={p.value}>
                            <input
                                type="checkbox"
                                disabled={applicantMode}
                                checked={products.includes(p.value)}
                                onChange={(e) =>
                                    updateParams({
                                        products: (e.target.checked
                                            ? [...products, p.value]
                                            : products.filter((x) => x !== p.value)
                                        ).join(','),
                                    })
                                }
                            />{' '}
                            {p.label}
                        </label>
                    ))}
                    {siteEnvironments.length > 0 ? (
                        <label>
                            Apply site (images and links){' '}
                            <select
                                data-testid="email-preview-site-environment"
                                value={siteEnvironment}
                                onChange={(e) =>
                                    updateParams({ site: e.target.value === 'Current' ? undefined : e.target.value })
                                }
                            >
                                {siteEnvironments.map((s) => (
                                    <option key={s} value={s}>
                                        {SITE_ENVIRONMENT_LABELS[s]}
                                    </option>
                                ))}
                            </select>
                        </label>
                    ) : null}
                    <label>
                        Applicant ID{' '}
                        <input
                            data-testid="email-preview-applicant-id"
                            placeholder="blank = sample data"
                            value={applicantId}
                            onChange={(e) => updateParams({ applicantId: e.target.value.trim() || undefined })}
                        />
                    </label>
                    {applicantId && !applicantIdValid ? <span>Enter a full applicant ID</span> : null}
                    {applicantNotFound ? <span>Applicant not found</span> : null}
                    {preview?.mode === 'applicant' ? (
                        <span>
                            Using applicant's partner/products: {preview.brandUsed} · {preview.productsUsed.join(', ')}
                        </span>
                    ) : null}
                </Controls>
                {notice ? <Notice role="status">{notice}</Notice> : null}
                {isLoadingPreview ? <LoadingIndicator show /> : null}
                {preview && !isLoadingPreview ? (
                    <>
                        <p>
                            <strong>Subject:</strong> {preview.subject} · <strong>From:</strong> {preview.from}{' '}
                            <ModeBadge>{preview.mode === 'applicant' ? `Applicant · ${preview.brandUsed}` : 'Sample'}</ModeBadge>
                        </p>
                        <EmailPreviewFrame html={preview.body} />
                    </>
                ) : null}
            </div>
        </PageLayout>
    );
};
